/**
 * Shared WidgetCtx builder — the data context every Exec widget renders from.
 * Extracted from DashboardPage (2026-06-12) so the Card Library review page
 * renders the SAME widgets from the SAME queries (TanStack dedupes by key —
 * mounting both costs nothing extra).
 */

import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "./api-context.js";
import { useDrill } from "./drill.js";
import { usePeriod } from "./period.js";
import { currentPeriodData } from "./period-query-data.js";
import type { WidgetCtx } from "./dashboard-widgets.js";
import { pageLoadState } from "./page-load-state.js";

export function widgetDerivedQueryReadiness(caps: WidgetCtx["caps"] | undefined): {
  financial: boolean;
  customerValue: boolean;
} {
  return {
    financial: caps?.financialBasis === "profit",
    customerValue: caps?.grossCustomerValue === true,
  };
}

export interface WidgetPageData {
  ctx: WidgetCtx;
  loadFailed: boolean;
  retrying: boolean;
  retry: () => void;
}

export function useWidgetCtx(ready: boolean): WidgetPageData {
  const api = useApiClient();
  const drill = useDrill();
  const navigate = useNavigate();
  const period = usePeriod();
  const caps = useQuery({ queryKey: ["capabilities"], enabled: ready, queryFn: () => api.get<WidgetCtx["caps"]>("/dashboard/capabilities"), staleTime: 0, refetchOnWindowFocus: true });
  const derivedReady = widgetDerivedQueryReadiness(caps.data);

  const overview = useQuery({ queryKey: ["dash-overview", period.cacheKey], enabled: ready, queryFn: () => api.get<{ metrics: WidgetCtx["overview"]; series: WidgetCtx["series"] }>("/dashboard/overview", { ...period.query }) });
  // Company-wide overview for the BLENDED MER widget: its denominator is account-
  // level spend (not per-product), so its revenue numerator must stay whole-company
  // too. `product: undefined` overrides the rail's injected product filter here only.
  const overviewCompany = useQuery({ queryKey: ["dash-overview-co", period.cacheKey], enabled: ready && derivedReady.financial, queryFn: () => api.get<{ metrics: WidgetCtx["overview"] }>("/dashboard/overview", { ...period.query, product: undefined, acqSource: undefined, acqAffiliate: undefined, acqDiscount: undefined }) });
  const cac = useQuery({
    queryKey: ["dash-cac", period.cacheKey],
    enabled: ready && derivedReady.financial,
    queryFn: () =>
      api.get<{
        cac: { blendedCac: number | null; totalNewCustomers: number; totalMarketingSpendBase: number };
        acquisitionCac: { blendedCac: number | null; totalNewCustomers: number; totalMarketingSpendBase: number } | null;
        acquisitionOverview: WidgetCtx["overview"] | null;
        coverage: { clamped: boolean; covered: boolean; note: string | null };
        ltvCacRatio: number | null;
      }>(
        "/dashboard/cac",
        { ...period.query },
      ),
  });
  const retention = useQuery({ queryKey: ["dash-retention", period.cacheKey], enabled: ready, queryFn: () => api.get<WidgetCtx["retention"]>("/dashboard/retention-overview", { ...period.query }) });
  // Avg-LTV KPIs are a LIFETIME lens (all-time matured cohorts), NOT the global
  // window's acquisition cohort — a 365-day LTV can't exist for a 30-day-old
  // cohort, which left the card blank at the default window (founder 2026-06-16).
  // The card-catalog already declares these "all-time"; the period-scoped query
  // was drift. /dashboard/ltv (gross) returns averageByTimeframe across all
  // matured cohorts, so every marker shows a real number.
  const ltv = useQuery({ queryKey: ["dash-ltv-alltime"], enabled: ready && derivedReady.customerValue, queryFn: () => api.get<WidgetCtx["ltv"]>("/dashboard/ltv") });
  const geo = useQuery({ queryKey: ["dash-geo", period.cacheKey], enabled: ready, queryFn: () => api.get<WidgetCtx["geo"]>("/dashboard/geography", { ...period.query }) });
  const products = useQuery({ queryKey: ["dash-products", period.cacheKey], enabled: ready, queryFn: () => api.get<WidgetCtx["products"]>("/products", { ...period.query }) });
  // Same key as the Profit Model section → TanStack dedupes (no extra request).
  // The Operational-drag card reads operationalDrag; the profit-equation widget
  // reads the shared break-even % (paybackPctToBreakEven, shown only while the
  // window hasn't crossed — paybackBreakEvenDay === null).
  const profitModel = useQuery({
    queryKey: ["profit-model", period.cacheKey],
    enabled: ready && derivedReady.financial,
    queryFn: () => api.get<NonNullable<WidgetCtx["profitModel"]> & { paybackBreakEvenDay: number | null; paybackPctToBreakEven: number | null }>("/dashboard/profit-model", { ...period.query }),
  });
  // Monthly overhead — feeds the (now movable) profit-equation widget. Same key
  // DashboardPage used, so TanStack dedupes (no extra request).
  const overhead = useQuery({ queryKey: ["overhead"], enabled: ready && derivedReady.financial, queryFn: () => api.get<{ overhead: { monthlyOverheadAmount: string } | null }>("/business-costs/overhead") });

  const overviewData = currentPeriodData(overview.data, overview.isPlaceholderData);
  const overviewCompanyData = currentPeriodData(overviewCompany.data, overviewCompany.isPlaceholderData);
  const cacData = currentPeriodData(cac.data, cac.isPlaceholderData);
  const retentionData = currentPeriodData(retention.data, retention.isPlaceholderData);
  const geoData = currentPeriodData(geo.data, geo.isPlaceholderData);
  const productsData = currentPeriodData(products.data, products.isPlaceholderData);
  const profitModelData = currentPeriodData(profitModel.data, profitModel.isPlaceholderData);

  const acquisitionCac = cacData?.coverage.covered === false ? null : cacData?.acquisitionCac ?? null;
  const acquisitionOverview = cacData?.coverage.clamped
    ? cacData.acquisitionOverview ?? undefined
    : overviewCompanyData?.metrics;
  const ctx: WidgetCtx = {
    overview: overviewData?.metrics,
    series: overviewData?.series,
    revenueGrossCompany: overviewCompanyData?.metrics?.current.revenueGrossBase,
    overviewCompany: overviewCompanyData?.metrics,
    acquisitionOverview,
    acquisitionRevenueGross: acquisitionOverview?.current.revenueGrossBase,
    acquisitionCoverageNote: cacData?.coverage.note,
    cac: acquisitionCac
      ? {
          blendedCac: acquisitionCac.blendedCac,
          totalNewCustomers: acquisitionCac.totalNewCustomers,
          totalMarketingSpendBase: acquisitionCac.totalMarketingSpendBase,
          ltvCacRatio: cacData?.ltvCacRatio ?? null,
        }
      : undefined,
    retention: retentionData,
    retentionLoading: retention.isLoading || retention.isPlaceholderData,
    ltv: ltv.data,
    geo: geoData,
    products: productsData,
    productsLoading: products.isLoading || products.isPlaceholderData,
    profitModel: profitModelData,
    // Only while this window's customers haven't crossed full break-even; then
    // the equation strip adds the shared "% of the way" line (same as the flagship).
    pctToBreakEven: profitModelData && profitModelData.paybackBreakEvenDay === null ? profitModelData.paybackPctToBreakEven : null,
    overhead: overhead.data?.overhead ? Number(overhead.data.overhead.monthlyOverheadAmount) : null,
    caps: caps.data,
    openDrill: drill.open,
    goTo: navigate,
    periodKey: period.key,
    periodLabel: period.label,
    // Custom-range-aware plumbing (#3): request params, cache token, window days,
    // and the drill window — so widget day-part strips + KPI drills follow a
    // custom calendar range instead of collapsing to 30d.
    periodQuery: period.query,
    periodCacheKey: period.cacheKey,
    periodWindowDays: period.windowDays,
    periodDrillWindow: period.drillWindow,
  };
  // Every request below feeds a visible Exec card. A failed request must stop
  // the page instead of turning that card into a dash, empty chart, or setup
  // notice. Disabled queries are not errors, so gross-only accounts remain safe.
  const load = pageLoadState(
    caps,
    overview,
    overviewCompany,
    cac,
    retention,
    ltv,
    geo,
    products,
    profitModel,
    overhead,
  );
  return {
    ctx,
    loadFailed: load.failed,
    retrying: load.retrying,
    retry: load.retry,
  };
}
